import { executeAgentNode } from "@/services/workflowEngine/agentNodeExecutor";
import { createEmptyRunState } from "@/types/workflowEngine";
import type { WorkflowNode } from "@/types/aiToolkit";
import { aiAgentsService } from "@/services/aiAgentsService";
import { communicationSurface, type CommunicationAnalyzeContentType } from "@shared/tokenSavings";

export interface RunAgentOnContentInput {
  agentId: string;
  subject: string;
  from: string;
  to?: string;
  date?: string;
  body: string;
  contentType?: CommunicationAnalyzeContentType;
}

export interface RunAgentOnContentResult {
  content: string;
  model: string;
  confidence?: number;
  quality?: number;
  parsed?: unknown;
}

const EMAIL_ANALYSIS_INSTRUCTION = `Analyze the message below and extract useful structured information in Markdown format:

- **Summary** — 2-3 sentence overview
- **Key entities** — people, companies, dates, amounts
- **Action items** — concrete follow-ups with owners if identifiable
- **Sentiment / urgency** — brief assessment
- **Suggested reply points** — bullet list if a response is warranted

Be concise and actionable.`;

const MEETING_ANALYSIS_INSTRUCTION = `Analyze the meeting transcript below and extract useful structured information in Markdown format:

- **Summary** — 2-3 sentence overview of the meeting
- **Key decisions** — main outcomes and agreements
- **Action items** — concrete follow-ups with owners if identifiable
- **Open questions** — unresolved topics or blockers
- **Follow-ups** — suggested next steps

Be concise and actionable.`;

const CLICKUP_ANALYSIS_INSTRUCTION = `Analyze the ClickUp task or document content below and extract useful structured information in Markdown format:

- **Summary** — 2-3 sentence overview
- **Status & ownership** — current state, assignees, due dates
- **Key details** — requirements, decisions, blockers
- **Action items** — concrete follow-ups with owners if identifiable
- **Related context** — links, dependencies, or cross-references

Be concise and actionable.`;

const TELEGRAM_ANALYSIS_INSTRUCTION = `Analyze the Telegram message below and extract useful structured information in Markdown format:

- **Summary** — 2-3 sentence overview
- **Key entities** — people, usernames, dates, links
- **Action items** — concrete follow-ups with owners if identifiable
- **Sentiment / urgency** — brief assessment
- **Suggested reply points** — bullet list if a response is warranted

Be concise and actionable.`;

function getAnalysisInstruction(contentType: CommunicationAnalyzeContentType = "email"): string {
  if (contentType === "meeting" || contentType === "zoom") return MEETING_ANALYSIS_INSTRUCTION;
  if (contentType === "clickup") return CLICKUP_ANALYSIS_INSTRUCTION;
  if (contentType === "telegram") return TELEGRAM_ANALYSIS_INSTRUCTION;
  return EMAIL_ANALYSIS_INSTRUCTION;
}

function buildMetadataBlock(input: RunAgentOnContentInput): string {
  return [
    input.subject && `Subject: ${input.subject}`,
    input.from && `From: ${input.from}`,
    input.to && `To: ${input.to}`,
    input.date && `Date: ${input.date}`,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildStructuredUserContent(input: RunAgentOnContentInput): string {
  const metadata = buildMetadataBlock(input);
  return `Classify and analyze the following message.

---
${metadata}

${input.body}`;
}

function buildMarkdownUserContent(input: RunAgentOnContentInput): string {
  return `${getAnalysisInstruction(input.contentType)}

---
${buildMetadataBlock(input)}

${input.body}`;
}

/**
 * Run an agent against communication service content (Gmail, Fireflies, ClickUp).
 * Uses the same executor as workflow agent nodes — skills, worker profile, custom instructions, output schema.
 */
export async function runAgentOnContent(
  input: RunAgentOnContentInput
): Promise<RunAgentOnContentResult> {
  const agent = await aiAgentsService.getAgentById(input.agentId);

  const state = createEmptyRunState({
    body: input.body,
    subject: input.subject,
    from: input.from,
    to: input.to,
    date: input.date,
    contentType: input.contentType ?? "email",
  });

  const node: WorkflowNode = {
    id: "service-analyze",
    type: "agent",
    position: { x: 0, y: 0 },
    data: {
      label: "Service analyze",
      agentId: input.agentId,
    },
  };

  const userContent = agent?.outputSchema
    ? buildStructuredUserContent(input)
    : buildMarkdownUserContent(input);

  const result = await executeAgentNode(node, state, {
    userContent,
    app: communicationSurface(input.contentType),
  });

  const err = result.parsed as { error?: string } | undefined;
  if (err?.error) {
    throw new Error(err.error);
  }

  if (!result.raw.trim()) {
    throw new Error("Agent returned empty analysis");
  }

  return {
    content: result.raw.trim(),
    model: result.model ?? "unknown",
    confidence: result.confidence,
    quality: result.quality,
    parsed: result.parsed,
  };
}
